import { getSubjects, getRoles } from './users';
import {
  GRADE_BAND_MAP,
  ENGLISH_LANGUAGE_ARTS,
  MATH,
  SCIENCE,
  SOCIAL_STUDIES,
  WORLD_LANGUAGE,
  SPECIAL_EDUCATION,
} from '../constants/userProfile';

/**
 * Map individual grade levels (e.g. ["K", "3", "5"]) to unique grade bands (e.g. ["K-2", "3-5"]).
 * Accepts the raw grade_level strings from diffitUser.grade_levels[].grade_level.
 */
export function getGradeBands(gradeLevelStrings) {
  const bands = new Set();
  for (const grade of gradeLevelStrings) {
    const band = GRADE_BAND_MAP[grade];
    if (band) bands.add(band);
  }
  return [...bands];
}

// Each entry: { segment, match, pills }
// match.subjects, match.gradeBands, and match.roles are each optional — omit to match any value.
// When multiple fields are present, ALL must match. Within a field, ANY value matching is sufficient.
// match.roles uses startsWith so 'intervention_specialist_multilingual' matches the full role string.
// First matching segment wins. 'default' (match: null) is the fallback.
//
// Segment matching logic (first match wins):
//
// Segment            | Criteria                                                        | Grade constraint
// -------------------|-----------------------------------------------------------------|----------------
// ELL                | Subject "English Language Learners"                              | Any
//                    | OR role starting with intervention_specialist_multilingual        |
// SPED               | Subject "Special Education"                                     | Any
//                    | OR role starting with intervention_specialist_students_with_ieps |
// Foreign Language   | Subject "World Language"                                        | Any
// Lower Elementary   | Any subject                                                     | K-2
// ELA                | Subject "English Language Arts"                                 | 3-12
// Math               | Subject "Math"                                                  | 3-12
// Science            | Subject "Science"                                               | 3-12
// Social Studies     | Subject "Social Studies"                                        | 3-12
// Upper Elementary   | Any subject (fallback for unrecognized subjects)                | 3-5
// Default            | (fallback — no criteria)                                        | Any
//
// Specialist roles (ELL, SPED, Foreign Language) are checked first and have no grade
// constraint — a multilingual intervention specialist always gets ELL pills, regardless
// of other subjects or grade levels. Subject configs are checked before Upper Elementary
// so that a 3-5 teacher with a recognized subject gets subject-specific pills. Upper
// Elementary catches 3-5 teachers whose subjects don't match any specific segment.
// Within each segment, subjects and roles are OR'd (either can trigger the match),
// while grade bands are AND'd when present.
//
// Slides leads every row except ELL and SPED, which have no Slides pill. To keep each
// row at six — HelperPills clamps to a single row above 848px — each segment gave up its
// least-selected pill per `Unlimited Helper Pill Selected` (US only, Mar–Aug 2026);
// foreign-language and upper-elementary had too little volume to call.
export const SEGMENT_PILL_CONFIGS = [
  // --- Specialist roles (subject-only, any grade) ---
  {
    segment: 'ell',
    match: {
      subjects: ['English Language Learners'],
      roles: ['intervention_specialist_multilingual'],
    },
    pills: [
      { id: 'scaffold', label: 'Scaffold' },
      { id: 'vocabulary', label: 'Vocabulary' },
      { id: 'bilingual', label: 'Bilingual' },
      { id: 'visual-supports', label: 'Visual Supports' },
      { id: 'sentence-frames', label: 'Sentence Frames' },
      { id: 'word-walls', label: 'Word Walls' },
    ],
  },
  {
    segment: 'sped',
    match: {
      subjects: [SPECIAL_EDUCATION],
      roles: ['intervention_specialist_students_with_ieps'],
    },
    pills: [
      { id: 'scaffold', label: 'Scaffold' },
      { id: 'reading-and-questions', label: 'Reading & Questions' },
      { id: 'accommodations', label: 'Accommodations' },
      { id: 'visual-supports', label: 'Visual Supports' },
      { id: 'sel', label: 'SEL' },
      { id: 'chunking', label: 'Chunking' },
    ],
  },
  {
    segment: 'foreign-language',
    match: { subjects: [WORLD_LANGUAGE] },
    pills: [
      { id: 'slides', label: 'Slides' },
      { id: 'scaffold', label: 'Scaffold' },
      { id: 'test-prep', label: 'Test Prep' },
      { id: 'dialogues', label: 'Dialogues' },
      { id: 'grammar', label: 'Grammar' },
      { id: 'vocabulary', label: 'Vocabulary' },
    ],
  },
  // --- Lower Elementary (grade-only, any subject) ---
  {
    segment: 'lower-elementary',
    match: { gradeBands: ['K-2'] },
    pills: [
      { id: 'slides', label: 'Slides' },
      { id: 'scaffold', label: 'Scaffold' },
      { id: 'stations', label: 'Stations' },
      { id: 'social-studies', label: 'Social Studies' },
      { id: 'sel', label: 'SEL' },
      { id: 'math', label: 'Math' },
    ],
  },
  // --- Subject specialists (grades 3-12) ---
  {
    segment: 'ela',
    match: { subjects: [ENGLISH_LANGUAGE_ARTS], gradeBands: ['3-5', '6-8', '9-12'] },
    pills: [
      { id: 'slides', label: 'Slides' },
      { id: 'scaffold', label: 'Scaffold' },
      { id: 'test-prep', label: 'Test Prep' },
      { id: 'comprehension', label: 'Comprehension' },
      { id: 'writing', label: 'Writing' },
      { id: 'discussion', label: 'Discussion' },
    ],
  },
  {
    segment: 'math',
    match: { subjects: [MATH], gradeBands: ['3-5', '6-8', '9-12'] },
    pills: [
      { id: 'slides', label: 'Slides' },
      { id: 'scaffold', label: 'Scaffold' },
      { id: 'test-prep', label: 'Test Prep' },
      { id: 'practice', label: 'Practice' },
      { id: 'warm-ups', label: 'Warm-Ups' },
      { id: 'real-world', label: 'Real-World' },
    ],
  },
  {
    segment: 'science',
    match: { subjects: [SCIENCE], gradeBands: ['3-5', '6-8', '9-12'] },
    pills: [
      { id: 'slides', label: 'Slides' },
      { id: 'scaffold', label: 'Scaffold' },
      { id: 'test-prep', label: 'Test Prep' },
      { id: 'reading-plus-questions', label: 'Reading & Questions' },
      { id: 'cer', label: 'CER' },
      { id: 'vocabulary', label: 'Vocabulary' },
    ],
  },
  {
    segment: 'social-studies',
    match: { subjects: [SOCIAL_STUDIES], gradeBands: ['3-5', '6-8', '9-12'] },
    pills: [
      { id: 'slides', label: 'Slides' },
      { id: 'scaffold', label: 'Scaffold' },
      { id: 'test-prep', label: 'Test Prep' },
      { id: 'study-guide', label: 'Study Guide' },
      { id: 'dbq', label: 'DBQ' },
      { id: 'reading-plus-questions', label: 'Reading & Questions' },
    ],
  },
  // --- Upper Elementary fallback (3-5 teachers with unrecognized subjects) ---
  {
    segment: 'upper-elementary',
    match: { gradeBands: ['3-5'] },
    pills: [
      { id: 'slides', label: 'Slides' },
      { id: 'scaffold', label: 'Scaffold' },
      { id: 'test-prep', label: 'Test Prep' },
      { id: 'math', label: 'Math' },
      { id: 'science', label: 'Science' },
      { id: 'writing', label: 'Writing' },
    ],
  },
  // --- Generic fallback ---
  {
    segment: 'default',
    match: null,
    pills: [
      { id: 'slides', label: 'Slides' },
      { id: 'scaffold', label: 'Scaffold' },
      { id: 'test-prep', label: 'Test Prep' },
      { id: 'math', label: 'Math' },
      { id: 'stations', label: 'Stations' },
      { id: 'study-guide', label: 'Study Guide' },
    ],
  },
];

// HelperPills renders a single row, so more than this wraps or clips. Also
// enforced by the landing-page admin editor and the API.
export const MAX_PILLS = 6;

/**
 * Resolve which pills to show for a given user's subjects, grade levels, and roles.
 * @param {string[]} subjects - e.g. ["Math", "English Language Arts"]
 * @param {string[]} gradeLevelStrings - individual grade strings, e.g. ["K", "1", "6"]
 * @param {string[]} roles - e.g. ["intervention_specialist_multilingual_learners"]
 */
export function getPillsForUser(subjects, gradeLevelStrings, roles = []) {
  const gradeBands = getGradeBands(gradeLevelStrings);

  for (const config of SEGMENT_PILL_CONFIGS) {
    if (!config.match) continue; // skip default during iteration

    // Subjects and roles are OR'd — either can satisfy the "who is this user" check.
    // Grade bands are AND'd with that result when present.
    const subjectHit = config.match.subjects?.some((s) => subjects.includes(s));
    const roleHit = config.match.roles?.some((prefix) => roles.some((r) => r.startsWith(prefix)));
    const identityMatch =
      !config.match.subjects && !config.match.roles ? true : subjectHit || roleHit;
    const gradeMatch =
      !config.match.gradeBands || config.match.gradeBands.some((g) => gradeBands.includes(g));

    if (identityMatch && gradeMatch) return config.pills;
  }

  // Fallback to default
  const defaultConfig = SEGMENT_PILL_CONFIGS.find((c) => c.match === null);
  return defaultConfig?.pills || [];
}

/**
 * Which helper pills a visitor sees.
 *
 * A campaign variant's pills are generic by construction, so they only win
 * where there is no profile to personalize from. That was always true on
 * /for/:slug, where nearly every visitor is logged out — but `/` carries a
 * variant now too and its population is the opposite, so a running campaign
 * must not strip a teacher's own subject and grade pills off the home page.
 *
 * Unknown sign-in state counts as signed out. Clerk reports `isSignedIn` as
 * undefined until it loads, and making the variant wait on that would cost the
 * campaign page's own visitors the pills it was authored for.
 */
export function resolveUserPills({ isSignedIn, isLDidentified, landingPageVariant, diffitUser }) {
  if (!isSignedIn && landingPageVariant?.pills?.length) {
    return landingPageVariant.pills.slice(0, MAX_PILLS);
  }
  // Profile-derived pills have to wait for the profile.
  if (!isLDidentified) return [];
  return getPillsForUser(
    getSubjects(diffitUser),
    diffitUser?.grade_levels?.map((gl) => gl.grade_level) || [],
    getRoles(diffitUser)
  );
}
